import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: '15+', suffix: '', label: 'Años de Experiencia' },
  { value: '500+', suffix: '', label: 'Proyectos Ejecutados' },
  { value: '200+', suffix: '', label: 'Toneladas Exportadas' },
  { value: '98', suffix: '%', label: 'Tasa de Satisfacción' },
];

function AnimatedCounter({ value, suffix }: { value: string; suffix: string }) {
  const [display, setDisplay] = useState('0');
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const numeric = parseInt(value.replace(/\D/g, ''), 10) || 0;
          const isPlus = value.includes('+');
          const duration = 1500;
          const start = performance.now();

          const tick = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(eased * numeric);
            setDisplay(current + (isPlus ? '+' : ''));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-2">
      {display}
      <span className="text-primary-500">{suffix}</span>
    </div>
  );
}

export default function StatsSection() {
  return (
    <section className="relative bg-background-50 py-16 md:py-20 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                <p className="text-foreground-500 text-xs md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="border-t border-background-200/60 my-12" />

          <div className="text-center">
            <p className="text-foreground-500 text-xs md:text-sm font-medium mb-6 tracking-wide uppercase">
              Sectores que Servimos
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {[
                'Perforación Offshore',
                'Perforación Onshore',
                'Producción de Crudo Pesado',
                'Producción de Crudo Liviano',
                'Refinación y Petroquímica',
                'Mantenimiento de Ductos',
              ].map((ind) => (
                <span
                  key={ind}
                  className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-xs md:text-sm font-medium border border-background-200/60"
                >
                  {ind}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}